/// <reference types="vite/client" />
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FileText,
  Bot, 
  Sun,
  Moon,
  Upload,
  ChevronDown,
  Wand2,
  Sparkles,
  Coins,
  Loader2,
  Copy,
  Check,
  AlertTriangle,
  Search, // Added search icon
  LogOut,
  MousePointerClick,
  EyeOff,
  X,
  CreditCard,
  ArrowRight,
  HelpCircle,
  ExternalLink,
  CheckCircle,
  Zap,
  BrainCircuit,
  ShieldCheck
} from 'lucide-react';
import Markdown from 'react-markdown';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { auth, googleProvider, db } from './firebase';
import { doc, setDoc, getDoc, onSnapshot, updateDoc, increment } from 'firebase/firestore';

import { LandingContent } from './components/LandingContent';
import { DetectorLandingContent } from './components/DetectorLandingContent';
import { PeelLandingContent } from './components/PeelLandingContent';
import { AuthModal } from './components/AuthModal';
import { TermsOfService } from './components/TermsOfService';
import { PeelHighlight } from './components/PeelHighlight';
import { GettingStartedModal } from './components/GettingStartedModal';
import toast, { Toaster } from 'react-hot-toast';
import { AnimatedHumanizingPreview } from './components/AnimatedHumanizingPreview';
import { motion } from 'motion/react';

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

const safeFetchJson = async (res: Response) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(text ? `Server error: ${text}` : `Empty response from server (Status ${res.status})`);
  }
};

export default function App() {
  const [showGettingStarted, setShowGettingStarted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hasSeenGettingStarted') !== 'true';
    }
    return false;
  });

  const handleCloseGettingStarted = () => {
    localStorage.setItem('hasSeenGettingStarted', 'true');
    setShowGettingStarted(false);
  };

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved) return saved === 'dark';
    }
    return true; // Default to dark mode for any user
  });
  const [activeView, setActiveView] = useState<'humanizer' | 'detector' | 'peel' | 'tos'>('humanizer');
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  
  // Auth state
  const [user, setUser] = useState<User | null>(null);

  // Detector state
  const [detectorText, setDetectorText] = useState('');
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectorResult, setDetectorResult] = useState<{
    classification: 'Human-written' | 'Mixed' | 'AI-generated';
    aiPercentage: number;
    humanPercentage: number;
    analysisPoints: string[];
    suspiciousTerms?: string[];
  } | null>(null);
  
  const [credits, setCredits] = useState(5);
  const [hasPaid, setHasPaid] = useState<boolean>(false);
  const [userRank, setUserRank] = useState(0);
  const [userIp, setUserIp] = useState<string>('');
  const [ipWordCount, setIpWordCount] = useState<number>(0);
  const [isAdminIP, setIsAdminIP] = useState<boolean>(false);
  
  const [showPricing, setShowPricing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<{url: string} | null>(null);
  const [showAuthModal, setShowAuthModal] = useState<'login' | 'signup' | null>(null);
  const [recentActions, setRecentActions] = useState<{name: string, cost: number, timestamp: string}[]>([]);
  
  // Citation Modal state
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [citationLink, setCitationLink] = useState('');
  const [isGeneratingCitation, setIsGeneratingCitation] = useState(false);
  
  // Style Tab State
  const [activeStyleTab, setActiveStyleTab] = useState('Free');

  // Smart Rewrite Modal state
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [rewriteStyle, setRewriteStyle] = useState('Free');
  const [selectedTextForRewrite, setSelectedTextForRewrite] = useState('');
  const [showHighlightPrompt, setShowHighlightPrompt] = useState(false);
  
  // Copy state
  const [isCopied, setIsCopied] = useState(false);

  // Form states
  const [subject, setSubject] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [brief, setBrief] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('draft_brief');
      if (saved) return saved;
    }
    return '';
  });
  const [projectGuidance, setProjectGuidance] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('draft_projectGuidance');
      if (saved) return saved;
    }
    return '';
  });
  const [sample, setSample] = useState('');
  const [expectations, setExpectations] = useState('');
  const [uploadedFilename, setUploadedFilename] = useState('');
  const [uploadedDocsContext, setUploadedDocsContext] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('draft_brief', brief);
    }
  }, [brief]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('draft_projectGuidance', projectGuidance);
    }
  }, [projectGuidance]);
  
  // App states
  const [stealthMode, setStealthMode] = useState(false);
  const [boostQuestions, setBoostQuestions] = useState<string[]>([]);
  const [boostAnswers, setBoostAnswers] = useState<string[]>(['', '', '']);
  const [generatedOutput, setGeneratedOutput] = useState('');
  
  // PEEL Generator state
  const [peelTopic, setPeelTopic] = useState('');
  const [peelDiscipline, setPeelDiscipline] = useState('English Literature');
  const [peelStealth, setPeelStealth] = useState(false);
  const [peelOutput, setPeelOutput] = useState<{ point?: string, evidence?: string, explanation?: string, link?: string, fullProse?: string, raw?: string } | null>(null);
  const [isGeneratingPeel, setIsGeneratingPeel] = useState(false);
  
  const parsePeelXml = (rawXml: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(rawXml, "text/xml");
      const hasParsingError = xmlDoc.querySelector("parsererror");
      
      const extract = (tag: string) => {
        if (!hasParsingError) {
          const el = xmlDoc.querySelector(tag);
          if (el) return el.textContent?.trim() || "";
        }
        const regex = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, "i");
        const match = rawXml.match(regex);
        return match ? match[1].trim() : "";
      };

      return {
        point: extract("point"),
        evidence: extract("evidence"),
        explanation: extract("explanation"),
        link: extract("link"),
        fullProse: extract("full_prose") || rawXml.replace(/<[^>]+>/g, '').trim(),
        raw: rawXml
      };
    } catch (e) {
      return { fullProse: rawXml.replace(/<[^>]+>/g, '').trim(), raw: rawXml };
    }
  };

  // Loading states
  const [isBoosting, setIsBoosting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [isScanningCitations, setIsScanningCitations] = useState(false);

  useEffect(() => {
    let interval: any;
    if (isGenerating || isHumanizing) {
    } else {
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating, isHumanizing]);

  const handledPaymentRef = useRef(false);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => {
         if (data.ip) {
            setUserIp(data.ip);
            // Fetch word count from firestore
            const ipRef = doc(db, 'guest_ips', data.ip.replace(/\//g, '_'));
            onSnapshot(ipRef, (snap) => {
               if (snap.exists()) {
                  if (typeof snap.data()?.usedWords === 'number') {
                     setIpWordCount(snap.data()?.usedWords);
                  }
                  if (snap.data()?.isAdminIP || data.ip === '195.180.35.173') {
                     setIsAdminIP(true);
                  }
               } else {
                  setDoc(ipRef, { usedWords: 0, isAdminIP: false }, { merge: true }).catch(() => {});
               }
            }, (error) => {
               console.error("Error fetching guest IPs", error);
               if (data.ip === '195.180.35.173') setIsAdminIP(true);
            });
         }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (userRank === 999 && userIp) {
       const ipRef = doc(db, 'guest_ips', userIp.replace(/\//g, '_'));
       setDoc(ipRef, { isAdminIP: true }, { merge: true }).catch(console.error);
    }
  }, [userRank, userIp]);

  useEffect(() => {
    let unsubSnap: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      const params = new URLSearchParams(window.location.search);
      const isPaymentSuccess = params.get('payment_success');
      const isPaymentCancelled = params.get('payment_cancelled');
      const isPaymentError = params.get('payment_error');
      
      let pendingCredits = 0;
      if (!handledPaymentRef.current) {
          if (isPaymentSuccess) {
              pendingCredits = parseInt(params.get('credits_added') || "0", 10);
              if (pendingCredits > 0) toast.success(`Payment successful! ${pendingCredits} credits have been added.`);
              else toast.success("Payment successful! Your credits have been updated.");
          } else if (isPaymentCancelled) {
              toast.error("Payment was cancelled.");
          } else if (isPaymentError) {
              toast.error("There was an issue processing your payment.");
          }
          
          if (isPaymentSuccess || isPaymentCancelled || isPaymentError) {
              window.history.replaceState({}, document.title, window.location.pathname);
          }
          handledPaymentRef.current = true;
      }

      if (currentUser) {
        // Save to firestore and get credits
        const expectedRank = (currentUser.email === 'jlnf2007@gmail.com' || currentUser.email === 'boxguy75@gmail.com' || currentUser.email === 'isobelo4678@colchester.ac.uk') ? 999 : 2;
        let currentCredits = 5;
        let isPaid = false;

        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userDocRef);
          
          if (userSnap.exists()) {
             if (userSnap.data()?.credits !== undefined && !isNaN(userSnap.data()?.credits)) {
                currentCredits = userSnap.data()?.credits;
                if (currentCredits < 0) {
                   currentCredits = 3;
                   await updateDoc(userDocRef, { credits: currentCredits });
                }
             } else {
                currentCredits = 5;
             }
             if (userSnap.data()?.hasPaid) isPaid = true;
          } else {
             // New user
             const storedCredits = localStorage.getItem('guest_credits');
             const localGuestCredits = storedCredits !== null ? Number(storedCredits) : 5;
             currentCredits = localGuestCredits + 2;
             // Clear guest credits so they don't get reused if they log out
             localStorage.removeItem('guest_credits');
          }
          
          if (pendingCredits > 0) {
              currentCredits += pendingCredits;
              isPaid = true;
          }
          
          setCredits(currentCredits);
          setHasPaid(isPaid);
          setUserRank(expectedRank);
          
          await setDoc(userDocRef, {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            lastLogin: new Date(),
            rank: expectedRank,
            credits: currentCredits,
            hasPaid: isPaid
          }, { merge: true });

          // Listen for credit changes
          if (unsubSnap) unsubSnap();
          unsubSnap = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data()?.credits !== undefined && !isNaN(docSnap.data()?.credits)) {
               let snapCredits = docSnap.data()?.credits;
               if (snapCredits < 0) {
                  snapCredits = 3;
                  updateDoc(userDocRef, { credits: 3 }).catch(console.error);
               }
               setCredits(snapCredits);
            }
          }, (err) => console.error("Credit snapshot error", err));
        } catch (error) {
          console.error("Error saving user to DB", error);
          if (pendingCredits > 0) {
              currentCredits += pendingCredits;
              isPaid = true;
          }
          setCredits(currentCredits);
          setHasPaid(isPaid);
          setUserRank(expectedRank);
        }

        // Trigger a simple heartbeat to the server to allow discord login hook to trigger
        setTimeout(async () => {
             const token = await currentUser.getIdToken();
             fetch(`${API_BASE_URL}/api/health`, {
                headers: { 'Authorization': `Bearer ${token}` }
             }).catch(() => {});
        }, 1000);
      } else {
         if (unsubSnap) {
             unsubSnap();
             unsubSnap = undefined;
         }
         const storedCredits = localStorage.getItem('guest_credits');
         let localGuestCredits = storedCredits !== null ? Number(storedCredits) : 5; // Guest gets 5 credits = 1500 words free (300 words per process)
         if (localGuestCredits < 0) {
             localGuestCredits = 3;
             localStorage.setItem('guest_credits', localGuestCredits.toString());
         }
         if (pendingCredits > 0) {
             localGuestCredits += pendingCredits;
             localStorage.setItem('guest_credits', localGuestCredits.toString());
         }
         setCredits(localGuestCredits);
      }
    });

    return () => {
      unsubscribe();
      if (unsubSnap) unsubSnap();
    };
  }, []);

  // Record usage helper (replaces addAction)

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  // Sync dark mode class and local storage
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      if (typeof window !== 'undefined') localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      if (typeof window !== 'undefined') localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const countWords = (t: string) => {
    if (!t) return 0;
    return t.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  const [selectedTopup, setSelectedTopup] = useState<'pack_150' | 'pack_50' | 'stealth_15' | 'stealth_9'>('pack_150');

  const handleTopUp = async () => {
    try {
        let headers: any = {
            "Content-Type": "application/json"
        };
        if (auth.currentUser) {
            const token = await auth.currentUser.getIdToken();
            headers["Authorization"] = `Bearer ${token}`;
        }
        
        const res = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
            method: "POST",
            headers,
            body: JSON.stringify({ productId: selectedTopup, frontendUrl: "https://undetectablecoursework.com" })
        });
        const text = await res.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch(e) {
            console.error("Failed to parse JSON response:", text);
            throw new Error(`Invalid response from server: ${text || res.statusText}`);
        }
        
        if (data.url) {
            setShowPricing(false);
            setShowPaymentModal({ url: data.url });
        } else {
            toast.error(data.error || "Failed to initiate payment");
        }
    } catch(e) {
        console.error(e);
        toast.error("An error occurred launching the payment gateway.");
    }
  };

  const checkCreditsLocal = (text: string, type: 'boost' | 'generate' | 'humanize' = 'boost') => {
    const isGuest = !auth.currentUser;
    const wordCount = countWords(text);
    let maxWords = isGuest ? 300 : 500;
    if (userRank === 999 || isAdminIP) maxWords = 25000;
    
    if (wordCount > maxWords) {
      toast.error(`Word limit exceeded! As a ${isGuest ? 'guest' : 'member'}, you are allowed up to ${maxWords} words per process.`);
      setShowPricing(true);
      return false;
    }

    let required = 1;
    if (type === 'generate') {
      required = stealthMode ? 2 : 1;
    }
    
    if (!hasPaid) {
      const freeLimit = (userRank === 999 || isAdminIP) ? 250000 : 1500;
      if (ipWordCount + wordCount > freeLimit) {
        toast.error("Your free words limit is up! Please upgrade or Quick Buy credits to continue.");
        setShowPricing(true);
        return false;
      }
    }
    
    if (credits < required) {
      toast.error("You have run out of credits! Please top up to continue.");
      setShowPricing(true);
      return false;
    }
    return true;
  };

  const recordUsageAndAction = (name: string, cost: number, textProcessed: string) => {
    setRecentActions(prev => [{name, cost, timestamp: new Date().toISOString()}, ...prev].slice(0, 10));
    
    const wordCount = countWords(textProcessed);
    if (!hasPaid && userIp) {
      const ipRef = doc(db, 'guest_ips', userIp.replace(/\//g, '_'));
      setDoc(ipRef, { usedWords: increment(wordCount) }, { merge: true }).catch(console.error);
    }
    
    if (auth.currentUser) {
       updateDoc(doc(db, 'users', auth.currentUser.uid), { credits: increment(-cost) }).catch(console.error);
    } else {
       setCredits(prev => {
          const updated = Math.max(0, prev - cost);
          localStorage.setItem('guest_credits', updated.toString());
          return updated;
       });
    }
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadedFilename(file.name);
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) {
        let msg = "Upload failed";
        try {
          const errData = await safeFetchJson(res);
          msg = errData.error || msg;
        } catch {
          msg = await res.text();
        }
        throw new Error(msg);
      }
      
      const data = await safeFetchJson(res);
      setUploadedDocsContext(prev => prev + "\n" + data.text);
    } catch (err: any) {
      toast.error(`Could not parse document: ${err.message}`);
      setUploadedFilename("");
    } finally {
      setIsUploading(false);
    }
  };

  const handleGeneratePeel = async () => {
    if (!peelTopic) return toast.error("Please enter the topic or raw evidence.");
    if (!checkCreditsLocal(peelTopic, 'generate')) return;

    // Slice to limit
    const words = peelTopic.trim().split(/\s+/).slice(0, 500).join(" ");

    setIsGeneratingPeel(true);
    setPeelOutput(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/peel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: words,
          discipline: peelDiscipline,
          stealthMode: peelStealth
        })
      });
      const data = await safeFetchJson(res);
      if (res.ok) {
        setPeelOutput(parsePeelXml(data.output));
        recordUsageAndAction(`Gen PEEL (${peelDiscipline})`, data.cost || 0, peelTopic);
      } else {
        toast.error(data.error);
      }
    } catch (e: any) {
      console.error(e);
      toast.error("Error generating PEEL paragraph.");
    } finally {
      setIsGeneratingPeel(false);
    }
  };

  const handleBoost = async () => {
    if (!brief) return toast.error("Please enter course brief first.");
    if (!checkCreditsLocal(brief, 'boost')) return;

    const finalSubject = subject === 'Custom' ? customSubject : subject;

    setIsBoosting(true);
    try {
      let headers: any = { 'Content-Type': 'application/json' };
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/api/boost`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          textToProcess: brief, 
          subject: finalSubject || 'General',
          expectations: expectations || 'Free mode expectations',
          credits 
        })
      });
      const data = await safeFetchJson(res);
      if (res.ok && data.questions && data.questions.length > 0) {
        setBoostQuestions(data.questions);
        setBoostAnswers(Array(data.questions.length).fill(''));
        recordUsageAndAction('Boost Questions', data.cost || 0, brief);
      } else {
        toast.error(data.error);
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "A network error occurred while reaching the server.");
    } finally {
      setIsBoosting(false);
    }
  };

  const handleReset = () => {
    setSubject('');
    setCustomSubject('');
    setBrief('');
    setProjectGuidance('');
    setSample('');
    setExpectations('');
    setUploadedFilename('');
    setUploadedDocsContext('');
    setBoostQuestions([]);
    setBoostAnswers(['', '', '']);
    setGeneratedOutput('');
    setStealthMode(false);
  };

  const handleGenerate = async () => {
    if (!brief) return toast.error("Please enter course brief.");
    if (countWords(brief) < 40) return toast.error("Please provide a longer brief (minimum 40 words) for accurate generation.");
    const fullTextContext = brief + " " + projectGuidance + " " + boostAnswers.join(" ") + " " + uploadedDocsContext + " " + sample;
    if (!checkCreditsLocal(fullTextContext, 'generate')) return;

    setIsGenerating(true);
    try {
        const answersText = boostQuestions.map((q, i) => `Q: ${q}\nA: ${boostAnswers[i]}`).join('\n\n');
        const finalSubject = subject === 'Custom' ? customSubject : subject;

      let headers: any = { 'Content-Type': 'application/json' };
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/api/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          textToProcess: brief,
          subject: finalSubject || 'General',
          expectations: expectations || 'Free',
          sampleText: sample,
          uploadedDocs: (uploadedDocsContext ? `${uploadedDocsContext}\n\n` : '') + (projectGuidance ? `Project Guidance:\n${projectGuidance}` : ''),
          boostAnswers: answersText,
          stealthMode,
          credits,
          activeStyleTab
        })
      });
      const data = await safeFetchJson(res);
      if (res.ok) {
        setGeneratedOutput(data.output);
        if (data.detectorResult) setDetectorResult(data.detectorResult);
        recordUsageAndAction('Generate Outline', data.cost || 0, fullTextContext);
      } else {
        toast.error(data.error);
      }
    } catch(e: any) {
      console.error(e);
      toast.error(e.message || "A network error occurred while reaching the server.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSmartRewriteClick = () => {
    const selectedText = window.getSelection()?.toString();
    if (!selectedText) {
       setShowHighlightPrompt(true);
       return;
    }
    setSelectedTextForRewrite(selectedText);
    setShowStyleModal(true);
  };

  const handleSmartRewriteConfirm = async () => {
    setShowStyleModal(false);
    if (!selectedTextForRewrite) return;
    if (!checkCreditsLocal(selectedTextForRewrite, 'humanize')) return;

    setIsHumanizing(true);
    try {
        let headers: any = { 'Content-Type': 'application/json' };
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(`${API_BASE_URL}/api/humanize`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                textToProcess: selectedTextForRewrite,
                sampleText: sample,
                uploadedDocs: uploadedDocsContext,
                rewriteStyle,
                credits
            })
        });
        const data = await safeFetchJson(res);
        if (res.ok) {
            setGeneratedOutput(prev => prev.replace(selectedTextForRewrite, data.output));
            if (data.detectorResult) setDetectorResult(data.detectorResult);
            recordUsageAndAction(`Humanize (${rewriteStyle})`, data.cost || 0, selectedTextForRewrite);
            // clear selection
            window.getSelection()?.removeAllRanges();
        } else {
            toast.error(data.error);
        }
    } catch (e: any) {
        console.error(e);
        toast.error(e.message || "A network error occurred while reaching the server.");
    } finally {
        setIsHumanizing(false);
        setSelectedTextForRewrite('');
    }
  };

  const handleManualHumanizeComplete = () => {
      setIsHumanizing(false);
  }

  const handleGenerateCitation = async () => {
    if (!citationLink) return toast.error("Please enter a link.");
    setIsGeneratingCitation(true);
    try {
        let headers: any = { 'Content-Type': 'application/json' };
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(`${API_BASE_URL}/api/citation`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ link: citationLink })
        });
        const data = await safeFetchJson(res);
        if (res.ok) {
            setGeneratedOutput(prev => prev + (prev ? '\n\n' : '') + data.citation);
            setShowCitationModal(false);
            setCitationLink('');
        } else {
            toast.error(data.error);
        }
    } catch(e) {
        console.error(e);
        toast.error("Failed to generate citation");
    } finally {
        setIsGeneratingCitation(false);
    }
  };

  const handleDetect = async () => {
    if (!detectorText) return toast.error("Please enter text to detect.");
    setIsDetecting(true);
    try {
        let headers: any = { 'Content-Type': 'application/json' };
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(`${API_BASE_URL}/api/detect`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ text: detectorText })
        });
        const data = await safeFetchJson(res);
        if (res.ok) {
            setDetectorResult(data);
        } else {
            toast.error(data.error);
        }
    } catch(e) {
        console.error(e);
        toast.error("Failed to analyze text");
    } finally {
        setIsDetecting(false);
    }
  };

  const handleScanCitations = async () => {
    if (!generatedOutput) return toast.error("Nothing to scan.");
    setIsScanningCitations(true);
    try {
      let headers: any = { 'Content-Type': 'application/json' };
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/api/scan-citations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: generatedOutput })
      });
      const data = await safeFetchJson(res);
      if (res.ok) {
         if (data.output) setGeneratedOutput(data.output);
         else toast.error("Nothing was returned from the citation generator.");
      } else {
         toast.error(data.error);
      }
    } catch(e) {
      console.error(e);
      toast.error("Failed to scan for citations");
    } finally {
      setIsScanningCitations(false);
    }
  };

  const handleOutputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const handleCopy = () => {
    if (generatedOutput) {
      const plainText = generatedOutput
        .replace(/^#+\s+/gm, '') // Remove headers
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/__(.*?)__/g, '$1') // Remove bold (underscores)
        // .replace(/\*(.*?)\*/g, '$1') // Remove italic (commented out to avoid messing up bullet points, though it usually works if matched pair)
        .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // Remove code blocks
        .replace(/~~(.*?)~~/g, '$1') // Remove strikethrough
        .replace(/\[(.*?)\]\(.*?\)/g, '$1'); // Remove links
        
      navigator.clipboard.writeText(plainText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative w-full bg-transparent text-zinc-900 dark:text-white">
      {showGettingStarted && (
        <GettingStartedModal isOpen={showGettingStarted} onClose={handleCloseGettingStarted} />
      )}
      <Toaster position="top-center" toastOptions={{ style: { background: "#333", color: "#fff" } }} />
      <Helmet>
        {activeView === 'humanizer' && (
          <>
            <title>AI Humanizer | UndetectableCoursework</title>
            <meta name="description" content="Humanize your AI-generated coursework to bypass detectors like Turnitin. Make your essays read naturally." />
            <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/` : ''} />
          </>
        )}
        {activeView === 'detector' && (
          <>
            <title>AI Content Detector & Free AI Checker | UndetectableCoursework</title>
            <meta name="description" content="Use our advanced AI detector and AI checker to accurately detect AI-generated text. Our ai humanizer tool helps humanize ai content to bypass detectors seamlessly." />
            <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/detector` : ''} />
          </>
        )}
        {activeView === 'peel' && (
          <>
            <title>PEEL Paragraph Generator & AI Humanize Tool | UndetectableCoursework</title>
            <meta name="description" content="Generate structured PEEL paragraphs and use our humanizer to humanize ai text. Bypass any ai detector with our powerful ai checker and ai humanizer tools." />
            <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/peel-generator` : ''} />
          </>
        )}
        {activeView === 'tos' && (
          <>
            <title>Terms of Service - AI Humanizer & AI Detector | UndetectableCoursework</title>
            <meta name="description" content="Review the Terms of Service for our ai detector, ai checker, ai humanizer, humanizer, and humanize ai tools." />
            <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/tos` : ''} />
          </>
        )}
      </Helmet>
      {/* Navbar (hidden in specific screenshot, but kept for functionality) */}
      <header className="border-b border-zinc-800/40 bg-zinc-50 dark:bg-[#111318]/80 backdrop-blur-md sticky top-0 z-10 hidden sm:block">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-zinc-900 flex items-center justify-center -ml-1">
              <Bot className="text-[#00ff00] w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">UndetectableCoursework</span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-900/70 dark:text-white/70">
            <button onClick={() => setActiveView('humanizer')} className={`transition-colors ${activeView === 'humanizer' ? 'text-green-600 dark:text-[#00ff00]' : 'hover:text-zinc-900 dark:hover:text-white'}`}>AI Humanizer</button>
            <button onClick={() => setActiveView('detector')} className={`transition-colors ${activeView === 'detector' ? 'text-green-600 dark:text-[#00ff00]' : 'hover:text-zinc-900 dark:hover:text-white'}`}>AI Detector</button>
            <div className="relative" onMouseEnter={() => setShowToolsMenu(true)} onMouseLeave={() => setShowToolsMenu(false)}>
              <div className="flex items-center gap-1 cursor-pointer hover:text-zinc-900 dark:hover:text-white transition-colors h-full py-2">
                Tools <ChevronDown className="w-3 h-3" />
              </div>
              {showToolsMenu && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="w-48 bg-white dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-xl shadow-xl py-2">
                    <button onClick={() => { setActiveView('peel'); setShowToolsMenu(false); }} className={`w-full text-left px-4 py-2 hover:bg-zinc-100 dark:hover:bg-[#2e3138] transition-colors ${activeView === 'peel' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900 dark:text-white'}`}>
                      PEEL Paragraph Generator
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setShowPricing(true)} className="hover:text-zinc-900 dark:hover:text-white transition-colors">Pricing</button>
          </nav>

          <div className="flex items-center gap-4">
            <button
               onClick={() => setIsDarkMode(!isDarkMode)} 
               className="p-1.5 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800/50 text-zinc-900/70 dark:text-white/70 transition-colors outline-none"
               title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} Mode`}
            >
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className="hidden sm:flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-900/70 dark:text-white/70 bg-zinc-800/50 px-2.5 py-1 rounded-full border border-zinc-700/50">
                <Coins className="w-3.5 h-3.5" />
                <span className="font-mono">{credits} Cr</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-900/70 dark:text-white/70 bg-zinc-800/50 px-2.5 py-1 rounded-full border border-zinc-700/50">
                <span className="font-mono">{hasPaid ? '∞ / 10,000 words' : (userRank === 999 || isAdminIP) ? 'Admin Unlimited IP' : `${Math.max(0, 1500 - ipWordCount)} / 1,500 words free`}</span>
              </div>
              {user && !user.isAnonymous ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full border border-white/10" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-[#2e3138] border border-white/10 flex items-center justify-center text-xs font-medium text-zinc-900/70 dark:text-white/70">
                         {(user.displayName || user.email || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex flex-col items-start leading-none gap-1">
                       <span className="text-sm font-medium text-zinc-900/90 dark:text-white/90">{user.displayName || user.email?.split('@')[0]}</span>
                       <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${(user.email === 'jlnf2007@gmail.com' || user.email === 'boxguy75@gmail.com') ? 'bg-[#00ff00]/20 text-green-600 dark:text-[#00ff00]' : 'bg-white/10 text-zinc-900/60 dark:text-white/60'}`}>{user.email === 'jlnf2007@gmail.com' || user.email === 'boxguy75@gmail.com' ? 'Admin' : 'Member'}</span>
                    </div>
                  </div>
                  <button onClick={handleLogout} className="text-sm font-medium text-zinc-900/40 dark:text-white/40 hover:text-red-400 transition-colors flex items-center gap-1 ml-2">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={() => setShowAuthModal('login')} className="text-sm font-medium hover:text-green-600 dark:text-[#00ff00] transition-colors px-2">Log in</button>
                  <button onClick={() => setShowAuthModal('signup')} className="text-sm font-medium bg-[#00ff00] text-black px-4 py-1.5 rounded-full hover:bg-[#00ff00] transition-colors shadow-lg shadow-[#00ff00]/10">
                    Sign up
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 mt-12 mb-10 text-center text-balance">
        <h1 className="font-heading text-4xl sm:text-[2.75rem] font-bold tracking-tight text-zinc-900 dark:text-white mb-4">
          Humanize your coursework
        </h1>
        <p className="text-[15px] sm:text-base text-zinc-900/70 dark:text-white/70 leading-relaxed max-w-2xl mx-auto">
          Our advanced <strong>ai humanizer</strong> clones your distinct writing style and comprehensively bypasses any <strong>ai detector</strong> with guaranteed 0% scores. Whether you need to <strong>humanize ai</strong> text or pass a strict university <strong>ai checker</strong>, our powerful <strong>humanizer</strong> ensures your coursework reads naturally.
        </p>
      </div>

      {/* Main App Workspace */}
      <main className="max-w-[1400px] mx-auto px-0 sm:px-4 w-full flex-grow mb-16 relative">
      
        {/* No boost modal */}
        
        {activeView === 'humanizer' ? (
          <div className="w-full bg-white dark:bg-[#1e2025] sm:border border-y sm:border-x border-zinc-200 dark:border-[#2e3138] sm:rounded-xl overflow-hidden shadow-2xl shadow-black/50 transition-all duration-300">
          
          <div className="border-b border-zinc-200 dark:border-[#2e3138] bg-zinc-100 dark:bg-[#181a1f]">
            <div className="flex w-full text-[13px] overflow-x-auto hide-scrollbar custom-scrollbar px-2 sm:px-4">
                {['Free', 'Standard', 'Academic', 'Simple', 'Flowing', 'Formal', 'Expand', 'Shorten'].map((tab, i) => (
                    <button 
                        key={tab} 
                        onClick={() => setActiveStyleTab(tab)}
                        className={`relative px-4 py-3.5 font-medium transition-all whitespace-nowrap text-center outline-none shrink-0 ${activeStyleTab === tab ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900/60 dark:text-white/60 hover:text-zinc-900/90 dark:hover:text-white/90 hover:bg-zinc-200/50 dark:hover:bg-white/5'}`}>
                        {tab}
                        {activeStyleTab === tab && (
                            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-green-600 dark:bg-[#00ff00] shadow-[0_-2px_10px_rgba(0,255,0,0.5)]"></div>
                        )}
                    </button>
                ))}
            </div>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200 dark:divide-[#2e3138] lg:min-h-[calc(100vh-240px)]">
             
            {/* Left Column: Input */}
            <div className="flex flex-col h-full">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row items-center gap-3 p-3 sm:p-4 border-b border-zinc-200 dark:border-[#2e3138] bg-white dark:bg-[#1e2025]">
                    <div className="relative w-full sm:w-auto flex-shrink-0 flex gap-2">
                        <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-green-600 dark:text-[#00ff00]">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 7h.01"/><path d="M17 7h.01"/><path d="M7 17h.01"/><path d="M17 17h.01"/></svg>
                            </div>
                            <select 
                                aria-label="Select Subject"
                                className="w-full sm:w-auto appearance-none bg-zinc-100 dark:bg-[#272a31] border border-zinc-300 dark:border-[#3e424d] rounded-md pl-9 pr-8 py-1.5 text-[13px] text-zinc-900/80 dark:text-white/80 outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            >
                                <option value="">Select Subject...</option>
                                <option value="Business Studies">Business Studies</option>
                                <option value="Adult Nursing">Adult Nursing</option>
                                <option value="Economics">Economics</option>
                                <option value="Sociology">Sociology</option>
                                <option value="Medicine">Medicine</option>
                                <option value="Design Studies">Design Studies</option>
                                <option value="Sport Science">Sport Science</option>
                                <option value="Computer Science">Computer Science</option>
                                <option value="Psychology">Psychology</option>
                                <option value="Law">Law</option>
                                <option value="Custom">Other (type here)</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-zinc-900/70 dark:text-white/70 pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" />
                        </div>
                        {subject === 'Custom' && (
                            <input 
                                className="w-full sm:w-40 bg-zinc-100 dark:bg-[#272a31] border border-zinc-300 dark:border-[#3e424d] rounded-md px-3 py-1.5 text-[13px] text-zinc-900/80 dark:text-white/80 outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors"
                                value={customSubject}
                                onChange={(e) => setCustomSubject(e.target.value)}
                                placeholder="Type subject..."
                                autoFocus
                            />
                        )}
                    </div>
                    
                    <label className={`flex-shrink-0 w-full sm:w-auto justify-center flex items-center gap-2 text-[13px] bg-zinc-100 dark:bg-[#272a31] border border-zinc-300 dark:border-[#3e424d] rounded-md px-3 py-1.5 transition-colors ${isUploading ? 'text-zinc-900/60 dark:text-white/60 cursor-not-allowed' : 'text-zinc-900/80 dark:text-white/80 hover:bg-[#343842] cursor-pointer'}`}>
                        {isUploading ? (
                          <div className="w-3.5 h-3.5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5" />
                        )}
                        <span className="hidden sm:inline">
                          {isUploading ? 'Parsing...' : (uploadedFilename || "Upload Docs")}
                        </span>
                        <input type="file" className="hidden" accept=".txt,.md,.csv,.json" onChange={handleFileUpload} disabled={isUploading} />
                    </label>
                    
                    {/* Hide expectations in this minimal view unless needed */}
                </div>
                
                {/* Main Textarea */}
                <textarea 
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="Paste your coursework guidelines and assignment prompt here..."
                    className="w-full min-h-[250px] flex-grow bg-transparent p-4 sm:p-5 text-zinc-900 dark:text-white placeholder:text-zinc-900/50 dark:placeholder:text-zinc-500 dark:placeholder:text-white/50 outline-none resize-none text-[14px]"
                    spellCheck="false"
                />

                {/* Project Guidance Textarea */}
                <div className="p-4 sm:p-5 border-t border-zinc-200 dark:border-[#2e3138] bg-white dark:bg-[#1e2025]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[11px] font-semibold tracking-wider text-zinc-900/60 dark:text-white/60 uppercase">Project Guidance / Notes</span>
                  </div>
                  <textarea 
                    value={projectGuidance}
                    onChange={(e) => setProjectGuidance(e.target.value)}
                    placeholder="Paste any specific project guidance, rubrics, or notes to ensure top-level academic quality..."
                    className="w-full h-20 bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-md p-3 text-[13px] text-zinc-900 dark:text-white placeholder:text-zinc-900/50 dark:placeholder:text-zinc-500 dark:placeholder:text-white/50 outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors resize-none"
                  />
                </div>

                {/* Profile Analyzer */}
                <div className="p-4 sm:p-5 border-t border-zinc-200 dark:border-[#2e3138] bg-white dark:bg-[#1e2025]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[11px] font-semibold tracking-wider text-zinc-900/60 dark:text-white/60 uppercase">Clone Writing Style (Optional)</span>
                    {sample.length > 5 && (
                      <span className="text-[10px] text-green-600 dark:text-[#00ff00] bg-[#00ff00]/10 px-2 py-0.5 rounded border border-green-600 dark:border-[#00ff00]/20">Style Provided</span>
                    )}
                  </div>
                  
                  <div className="relative">
                    <textarea 
                      value={sample}
                      onChange={(e) => setSample(e.target.value)}
                      placeholder="Paste 0-200 words of your previous writing to perfectly clone your tone & transitions..."
                      className="w-full h-16 bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-md p-3 text-[13px] text-zinc-900 dark:text-white placeholder:text-zinc-900/50 dark:placeholder:text-zinc-500 dark:placeholder:text-white/50 outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors resize-none"
                    />
                  </div>

                  {boostQuestions.length > 0 && (
                     <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-[#2e3138]">
                        <span className="text-[11px] font-semibold tracking-wider text-zinc-900/60 dark:text-white/60 uppercase mb-3 block">Boost Context</span>
                        <div className="space-y-3">
                           {boostQuestions.map((q, i) => (
                             <div key={i} className="text-[13px]">
                                <label className="block text-zinc-900/70 dark:text-white/70 mb-1">{q}</label>
                                <input 
                                  className="w-full bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-md px-3 py-1.5 text-zinc-900 dark:text-white focus:border-green-600 dark:border-[#00ff00] outline-none"
                                  value={boostAnswers[i]}
                                  onChange={(e) => {
                                      const newAnswers = [...boostAnswers];
                                      newAnswers[i] = e.target.value;
                                      setBoostAnswers(newAnswers);
                                  }}
                                  placeholder="Answer briefly..."
                                />
                             </div>
                           ))}
                        </div>
                     </div>
                  )}
                </div>

                {/* Footer Controls */}
                <div className="p-3 sm:p-5 border-t border-zinc-200 dark:border-[#2e3138] flex flex-wrap items-center justify-between gap-4 bg-white dark:bg-[#1e2025]">
                   <div className="flex flex-col gap-1.5 w-full sm:max-w-[200px] xl:max-w-xs">
                      <div className="flex items-center justify-between">
                         <span className={`text-xs font-mono font-medium ${countWords(brief) > 1000 ? 'text-red-500 dark:text-red-400' : 'text-zinc-900/60 dark:text-white/60'}`}>
                           {countWords(brief)} / 1000 words
                         </span>
                         {countWords(brief) > 1000 && (
                           <span className="text-[10px] text-red-500 dark:text-red-400 font-bold uppercase tracking-wider">Over Limit</span>
                         )}
                      </div>
                      <div className="w-full h-1.5 bg-zinc-100 dark:bg-[#2e3138]/50 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${countWords(brief) > 1000 ? 'bg-red-500 dark:bg-red-500' : countWords(brief) > 800 ? 'bg-yellow-500 dark:bg-yellow-400' : 'bg-green-600 dark:bg-[#00ff00]'}`} 
                          style={{ width: `${Math.min((countWords(brief) / 1000) * 100, 100)}%` }} 
                        />
                      </div>
                   </div>
                   
                   <div className="flex items-center flex-wrap gap-4 w-full 2xl:w-auto xl:justify-end">
                     <button
                       onClick={handleReset}
                       className="text-xs font-medium text-zinc-900/60 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white transition-colors bg-zinc-100 dark:bg-[#2e3138]/60 hover:bg-zinc-200 dark:hover:bg-[#2e3138] px-3 py-1.5 rounded-md border border-zinc-200 dark:border-[#3e424d] mr-2"
                     >
                       Reset
                     </button>
                     <label className="flex items-center gap-2.5 cursor-pointer group select-none mr-2">
                       <div className={`w-10 h-5 rounded-full flex items-center p-0.5 transition-colors ${stealthMode ? 'bg-[#00ff00]' : 'bg-zinc-300 dark:bg-zinc-600'}`}>
                         <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${stealthMode ? 'translate-x-5' : 'translate-x-0'}`}></div>
                       </div>
                       <input type="checkbox" className="hidden" checked={stealthMode} onChange={() => setStealthMode(!stealthMode)} />
                       <div className="flex flex-col">
                         <div className="flex items-center gap-1.5">
                           <span className={`text-[13px] font-semibold ${stealthMode ? 'text-zinc-900 dark:text-[#00ff00]' : 'text-zinc-900/80 dark:text-white/80'} whitespace-nowrap`}>Stealth Mode</span>
                           <div className="group/tooltip relative flex items-center">
                             <HelpCircle className="w-3.5 h-3.5 text-zinc-900/40 dark:text-white/40 hover:text-zinc-900/70 dark:hover:text-white/70 transition-colors outline-none cursor-help" />
                             <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[220px] px-3 py-2 bg-zinc-800 dark:bg-[#2e3138] text-white text-[11px] font-medium rounded shadow-xl opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity text-center z-10 hidden sm:block leading-relaxed">
                               Guarantees 0% AI detection by simulating human cognitive burstiness. Costs 2 credits.
                               <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-zinc-800 dark:border-t-[#2e3138]"></div>
                             </div>
                           </div>
                         </div>
                         <span className="text-[10px] text-zinc-500 font-mono hidden sm:block">Costs 2 Credits</span>
                       </div>
                     </label>

                     <button 
                       onClick={handleBoost}
                       disabled={isBoosting}
                       className="text-[13px] font-semibold px-5 py-2.5 rounded-lg bg-zinc-800 text-white dark:bg-[#2e3138] dark:text-white hover:bg-zinc-700 dark:hover:bg-[#3e424d] flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-sm active:scale-95 whitespace-nowrap flex-1 lg:flex-none"
                     >
                       {isBoosting ? 'Thinking...' : 'Boost Prompt Output'}
                     </button>
                     <button
                       onClick={handleGenerate}
                       disabled={isGenerating}
                       className="text-[13px] font-bold px-6 py-2.5 rounded-lg bg-[#00ff00] text-black hover:bg-[#00dd00] transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,255,0,0.15)] hover:shadow-[0_0_25px_rgba(0,255,0,0.3)] flex items-center justify-center gap-2 active:scale-95 whitespace-nowrap flex-1 lg:flex-none"
                     >
                       {isGenerating ? 'Generating...' : 'Generate Coursework'}
                     </button>
                   </div>
                </div>
            </div>

            {/* Right Column: Output */}
            <div className="flex flex-col bg-white dark:bg-[#1e2025] h-full">
                {/* Toolbar */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-[#2e3138] h-[61px] bg-white dark:bg-[#1e2025]">
                   <div className="flex items-center gap-3">
                      <span className="text-[13px] font-semibold text-zinc-900 dark:text-white">Output Document</span>
                      {detectorResult && (
                         <div className="flex items-center gap-2 bg-zinc-50 dark:bg-[#111318] px-2 py-0.5 md:px-3 md:py-1 rounded-full border border-zinc-200 dark:border-[#2e3138]/60">
                             <span className="hidden sm:inline text-[9px] md:text-[10px] text-zinc-900/50 dark:text-white/50 font-medium">Score:</span>
                             <div className="flex items-center gap-1 min-w-[50px] md:min-w-[70px]">
                                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full" style={{ backgroundColor: detectorResult.classification === 'Human-written' ? '#00ff00' : detectorResult.classification === 'Mixed' ? '#fbbf24' : '#ef4444' }}></div>
                                <span className="text-[10px] md:text-[11px] font-mono font-semibold whitespace-nowrap" style={{ color: detectorResult.classification === 'Human-written' ? '#00ff00' : detectorResult.classification === 'Mixed' ? '#fbbf24' : '#ef4444' }}>{detectorResult.humanPercentage}% H</span>
                             </div>
                             <div className="w-12 md:w-16 h-1 flex bg-zinc-200 dark:bg-[#2e3138] rounded-full overflow-hidden">
                                <div className="h-full bg-[#00ff00] transition-all duration-500" style={{ width: `${detectorResult.humanPercentage}%`, backgroundColor: detectorResult.classification === 'Human-written' ? '#00ff00' : detectorResult.classification === 'Mixed' ? '#fbbf24' : '#ef4444' }}></div>
                             </div>
                         </div>
                      )}
                   </div>
                   
                   <div className="flex items-center gap-6">

                      <div className="flex gap-2.5">
                         <button 
                           onClick={handleCopy}
                           disabled={!generatedOutput}
                           className="flex items-center gap-1.5 text-[11px] font-medium bg-white dark:bg-[#1e2025] border border-zinc-200 dark:border-[#2e3138] rounded px-2.5 py-1 text-zinc-900/70 dark:text-white/70 hover:text-zinc-900/90 dark:hover:text-white/90 hover:bg-zinc-200 dark:bg-[#25282f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {isCopied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                            {isCopied ? 'Copied' : 'Copy'}
                         </button>
                         <button 
                           onClick={handleScanCitations}
                           disabled={isScanningCitations || !generatedOutput}
                           className="flex items-center gap-1.5 text-[11px] font-medium bg-[#1a2133] border border-[#232d45] rounded px-2.5 py-1 text-blue-400 hover:bg-[#1f2840] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {isScanningCitations ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                            {isScanningCitations ? 'Scanning...' : 'Auto Citation'}
                         </button>
                         <button 
                           onClick={() => {
                             handleSmartRewriteClick();
                             handleManualHumanizeComplete(); 
                           }}
                           className="flex items-center gap-1.5 text-[11px] font-medium bg-[#142e31] border border-[#1b3d41] rounded px-2.5 py-1 text-green-600 dark:text-[#00ff00] hover:bg-[#173539] transition-colors"
                         >
                            <Sparkles className="w-3 h-3" />
                            {isHumanizing ? 'Humanizing...' : 'Smart Rewrite'}
                         </button>
                      </div>
                   </div>
                </div>

                {/* Output View */}
                <div className="flex-grow p-0 relative bg-zinc-50 dark:bg-[#121318] min-h-[500px] flex flex-col border-l border-zinc-200 dark:border-[#2e3138]">
                   {isGenerating || isHumanizing ? (
                      <div className="p-6 md:p-10 h-full flex flex-col">
                        <AnimatedHumanizingPreview 
                           isGenerating={isGenerating} 
                           isHumanizing={isHumanizing} 
                           stealthMode={stealthMode} 
                        />
                      </div>
                   ) : generatedOutput ? (
                      <motion.div 
                         initial={{ opacity: 0, scale: 0.98 }}
                         animate={{ opacity: 1, scale: 1 }}
                         transition={{ duration: 0.4, ease: "easeOut" }}
                         className="flex flex-col items-center py-10 px-4 sm:px-8 overflow-y-auto h-full w-full"
                      >
                         <div 
                            tabIndex={0}
                            title="Your Generated Coursework"
                            onKeyDown={handleOutputKeyDown}
                            className="w-full max-w-3xl bg-white dark:bg-[#1c1e24] shadow-sm ring-1 ring-zinc-200 dark:ring-white/10 rounded-lg selection:bg-green-500/30 p-8 sm:p-12 text-zinc-900/90 dark:text-white/90 text-[15px] leading-8 font-sans markdown-body outline-none focus:ring-2 focus:ring-[#00ff00]/50 transition-shadow mx-auto custom-scrollbar"
                         >
                            <Markdown>{generatedOutput}</Markdown>
                         </div>
                      </motion.div>
                   ) : (
                      <div className="h-full flex items-center justify-center p-6">
                         <div className="flex flex-col items-center text-center max-w-sm">
                           <FileText className="w-12 h-12 text-zinc-300 dark:text-zinc-700 mb-4" />
                           <p className="text-zinc-500 dark:text-zinc-400 font-medium text-[14px]">Your generated coursework will appear here</p>
                           <p className="text-zinc-400 dark:text-zinc-500 text-[12px] mt-2">Adjust your settings and click generate to begin the synthesis process.</p>
                         </div>
                      </div>
                   )}
                </div>

                 {/* Footer Status */}
                 <div className="p-4 border-t border-zinc-200 dark:border-[#2e3138] flex items-center justify-between">
                   <span className="text-[11px] text-zinc-900/60 dark:text-white/60 font-mono">{countWords(generatedOutput)} words</span>
                </div>
            </div>

          </div>
        </div>
        ) : activeView === 'detector' ? (
          <div className="w-full max-w-4xl mx-auto bg-white dark:bg-[#1e2025] sm:border border-y sm:border-x border-zinc-200 dark:border-[#2e3138] sm:rounded-xl overflow-hidden shadow-2xl shadow-black/50 p-4 sm:p-6">
             <div className="flex items-center justify-between mb-4">
               <h2 className="text-lg font-bold text-zinc-900 dark:text-white font-heading">AI Content Detector</h2>
               <button 
                  onClick={handleDetect}
                  disabled={!detectorText || isDetecting}
                  className="bg-[#00ff00] text-black px-4 py-1.5 rounded-md text-[13px] font-bold shadow-lg shadow-[#00ff00]/20 hover:bg-[#00ff00] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[90px]"
               >
                  {isDetecting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-black" /> : 'Analyze'}
               </button>
             </div>
             
             <textarea
                value={detectorText}
                onChange={(e) => setDetectorText(e.target.value)}
                placeholder="Paste text here to analyze for AI presence..."
                className="w-full min-h-[180px] sm:min-h-[300px] lg:min-h-[400px] h-full bg-zinc-50 dark:bg-[#111318] border border-zinc-200 dark:border-[#2e3138] rounded-lg p-3 text-zinc-900 dark:text-white text-[13px] leading-relaxed resize-none outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors mb-4 custom-scrollbar"
                spellCheck="false"
             />

             {detectorResult && (
                <div className="bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-lg overflow-hidden shadow-inner shadow-black/20">
                   <div className="p-4 border-b border-zinc-200 dark:border-[#2e3138] flex flex-col sm:flex-row sm:items-center justify-between bg-white dark:bg-[#1e2025] gap-4">
                      <div className="flex items-center gap-3">
                         <span className="text-[13px] font-semibold text-zinc-900/80 dark:text-white/80">Classification:</span>
                         <span className={`px-3 py-1 rounded text-[12px] font-bold tracking-wide ${
                            detectorResult.classification === 'Human-written' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 
                            detectorResult.classification === 'Mixed' ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' : 
                            'bg-red-500/10 text-red-500 border border-red-500/20'
                         }`}>
                           {detectorResult.classification}
                         </span>
                      </div>
                      <div className="flex items-center gap-5">
                         <div className="flex flex-col items-center">
                             <span className="text-[9px] text-zinc-900/60 dark:text-white/60 uppercase tracking-widest mb-0.5">Human</span>
                             <span className="text-base font-bold text-green-400">{detectorResult.humanPercentage}%</span>
                         </div>
                         <div className="w-px h-6 bg-zinc-200 dark:bg-[#2e3138]"></div>
                         <div className="flex flex-col items-center">
                             <span className="text-[9px] text-zinc-900/60 dark:text-white/60 uppercase tracking-widest mb-0.5">AI</span>
                             <span className="text-base font-bold text-red-400">{detectorResult.aiPercentage}%</span>
                         </div>
                      </div>
                   </div>
                   
                   {detectorResult.classification !== 'Human-written' && (
                       <div className="p-3 bg-red-500/5 border-b border-zinc-200 dark:border-[#2e3138] flex items-center justify-between">
                           <span className="text-[12px] text-red-400/90 font-medium">Text flagged as potentially AI-generated.</span>
                           <button 
                              onClick={() => {
                                 setBrief(detectorText);
                                 setActiveView('humanizer');
                              }}
                              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-3 py-1 rounded text-[12px] font-bold transition-colors flex items-center gap-1.5"
                           >
                              <Wand2 className="w-3.5 h-3.5" />
                              Humanize Text
                           </button>
                       </div>
                   )}
                   
                   {detectorResult.analysisPoints && detectorResult.analysisPoints.length > 0 && (
                       <div className="p-4 border-b border-zinc-200 dark:border-[#2e3138] bg-zinc-50 dark:bg-[#111318]">
                          <h3 className="text-[10px] font-bold text-green-600 dark:text-[#00ff00]/80 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                             <Sparkles className="w-3 h-3" />
                             Analysis Breakdown
                          </h3>
                          <ul className="space-y-3">
                            {detectorResult.analysisPoints.map((point, i) => (
                               <li key={i} className="text-[12px] text-zinc-900/70 dark:text-white/70 leading-relaxed flex items-start gap-2.5">
                                  <div className="w-1 h-1 rounded-full bg-[#00ff00]/40 mt-1.5 shrink-0" />
                                  <span className="flex-1">{point}</span>
                               </li>
                            ))}
                          </ul>
                       </div>
                   )}

                   {detectorResult.suspiciousTerms && detectorResult.suspiciousTerms.length > 0 && (
                       <div className="p-4 border-b border-zinc-200 dark:border-[#2e3138] bg-zinc-100 dark:bg-[#181a1f]">
                          <h3 className="text-[10px] font-bold text-red-400/80 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                             <AlertTriangle className="w-3 h-3" />
                             Suspicious Vocabulary Highlighted
                          </h3>
                          <div className="flex flex-wrap gap-2">
                             {detectorResult.suspiciousTerms.map((term, i) => (
                                 <span key={i} className="px-2.5 py-1 rounded bg-red-500/10 border border-red-500/20 text-red-400 font-mono text-[11px] font-medium tracking-wide">
                                     "{term}"
                                 </span>
                             ))}
                          </div>
                       </div>
                   )}

                   <div className="p-4 bg-zinc-100 dark:bg-[#181a1f]">
                      <div className="text-[10px] font-semibold text-zinc-900/50 dark:text-white/50 uppercase tracking-widest mb-3">Compatible with:</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                         {['Turnitin', 'OriginalityAI', 'Gowinston.ai', 'GPTZero'].map(site => (
                            <div key={site} className="flex items-center justify-between p-2 rounded-md bg-zinc-50 dark:bg-[#111318] border border-zinc-200 dark:border-[#2e3138]/60">
                               <span className="text-[11px] font-medium text-zinc-900/60 dark:text-white/60">{site}</span>
                               <Check className="w-3 h-3 text-green-500/50" />
                            </div>
                         ))}
                      </div>
                   </div>
                </div>
             )}
          </div>
        ) : activeView === 'peel' ? (
          <div className="w-full max-w-6xl mx-auto bg-white dark:bg-[#1e2025] sm:border border-y sm:border-x border-zinc-200 dark:border-[#2e3138] sm:rounded-xl overflow-hidden shadow-2xl shadow-black/50 p-4 sm:p-6 md:p-8">
             <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 pb-6 border-b border-zinc-100 dark:border-[#2e3138]">
               <div>
                 <h2 className="text-xl font-bold text-zinc-900 dark:text-white font-heading tracking-tight mb-1">Academic PEEL Generator</h2>
                 <p className="text-sm text-zinc-500 dark:text-zinc-400">Generate 0% AI-detected, flawless Point-Evidence-Explanation-Link paragraphs.</p>
               </div>
               <button 
                  onClick={handleGeneratePeel}
                  disabled={!peelTopic || isGeneratingPeel}
                  className="bg-[#00ff00] text-black px-6 py-2.5 rounded-lg text-sm font-bold shadow-lg shadow-[#00ff00]/10 hover:bg-[#00e600] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[160px]"
               >
                  {isGeneratingPeel ? (
                    <><Loader2 className="w-4 h-4 animate-spin text-black" /> Processing...</>
                  ) : (
                    <><Zap className="w-4 h-4" /> Generate PEEL</>
                  )}
               </button>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 border border-zinc-200 dark:border-[#2e3138] rounded-xl p-4 bg-zinc-50/50 dark:bg-[#111318]/50">
                 <div>
                    <label className="block text-[11px] font-bold text-zinc-900/60 dark:text-white/60 mb-2 uppercase tracking-wider">Academic Discipline</label>
                    <div className="relative">
                      <select
                          value={peelDiscipline}
                          onChange={(e) => setPeelDiscipline(e.target.value)}
                          className="w-full appearance-none bg-white dark:bg-[#1e2025] border border-zinc-300 dark:border-[#3e424d] text-zinc-900 dark:text-white rounded-lg p-3 pr-10 text-[13px] focus:outline-none focus:border-green-600 dark:focus:border-[#00ff00] transition-colors shadow-sm"
                      >
                          <option value="English Literature">English Literature</option>
                          <option value="History">History</option>
                          <option value="Empirical Sciences">Empirical Sciences</option>
                          <option value="Applied Business & HR">Applied Business & HR</option>
                          <option value="General Academic">General Academic</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-zinc-500">
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </div>
                 </div>
                 
                 <div className="flex flex-col justify-end">
                    <button
                        onClick={() => setPeelStealth(!peelStealth)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all shadow-sm ${peelStealth ? 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/50 text-indigo-700 dark:text-indigo-400' : 'bg-white dark:bg-[#1e2025] border-zinc-300 dark:border-[#3e424d] text-zinc-500 dark:text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-500'}`}
                    >
                        <div className="flex items-center gap-2">
                          <Zap className={`w-4 h-4 ${peelStealth ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
                          <span className="text-[13px] font-bold tracking-tight">Stealth Mode (0% AI)</span>
                        </div>
                        <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${peelStealth ? 'bg-indigo-500' : 'bg-zinc-200 dark:bg-[#2e3138]'}`}>
                          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${peelStealth ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                    </button>
                    <p className="text-[10px] text-zinc-500 mt-1.5 px-1">Guarantees highest humanization & Turnitin bypass.</p>
                 </div>
             </div>

             <div className="mb-6">
               <div className="flex justify-between items-end mb-2 px-1">
                 <label className="block text-[11px] font-bold text-zinc-900/60 dark:text-white/60 uppercase tracking-wider">Topic or Raw Evidence</label>
                 <div className={`text-[11px] font-mono ${countWords(peelTopic) > 500 ? 'text-yellow-600 dark:text-yellow-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
                   {countWords(peelTopic)} / 500 words
                 </div>
               </div>
               <PeelHighlight 
                  value={peelTopic}
                  onChange={(e: any) => setPeelTopic(e.target.value)}
                  wordLimit={500}
               />
             </div>

             {peelOutput && (
                <div className="mt-8 border border-green-600/30 dark:border-[#00ff00]/30 rounded-xl overflow-hidden bg-green-50/30 dark:bg-[#111318] shadow-lg shadow-green-900/5">
                   <div className="p-4 border-b border-green-600/20 dark:border-[#00ff00]/20 bg-green-100/50 dark:bg-[#181a1f] flex justify-between items-center">
                       <div className="flex items-center gap-2">
                         <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                         <h3 className="text-xs font-bold font-heading text-green-800 dark:text-[#00ff00] uppercase tracking-wider">Top-Tier Generated PEEL</h3>
                       </div>
                       <button
                          onClick={() => {
                            const textToCopy = peelOutput.fullProse || peelOutput.raw?.replace(/<[^>]+>/g, '').trim() || "";
                            navigator.clipboard.writeText(textToCopy);
                            toast.success("Copied to clipboard!");
                          }}
                          className="text-green-700 dark:text-[#00ff00]/70 hover:text-green-900 dark:hover:text-[#00ff00] transition flex items-center gap-1.5 text-xs font-bold bg-green-200/50 dark:bg-[#00ff00]/10 px-2.5 py-1 rounded-md"
                       >
                          <Copy className="w-3.5 h-3.5" /> Copy Text
                       </button>
                   </div>
                   <div className="p-5 sm:p-6 text-[14px] text-zinc-800 dark:text-gray-200 leading-[1.8] font-serif relative">
                         {peelOutput.point && (
                             <div className="mb-4">
                                 <h4 className="font-bold text-zinc-900 dark:text-gray-100 text-[15px] mb-1">Point</h4>
                                 <p>{peelOutput.point}</p>
                             </div>
                         )}
                         {peelOutput.evidence && (
                             <div className="mb-4">
                                 <h4 className="font-bold text-zinc-900 dark:text-gray-100 text-[15px] mb-1">Evidence</h4>
                                 <p>{peelOutput.evidence}</p>
                             </div>
                         )}
                         {peelOutput.explanation && (
                             <div className="mb-4">
                                 <h4 className="font-bold text-zinc-900 dark:text-gray-100 text-[15px] mb-1">Explanation</h4>
                                 <p>{peelOutput.explanation}</p>
                             </div>
                         )}
                         {peelOutput.link && (
                             <div className="mb-6">
                                 <h4 className="font-bold text-zinc-900 dark:text-gray-100 text-[15px] mb-1">Link</h4>
                                 <p>{peelOutput.link}</p>
                             </div>
                         )}
                         {(peelOutput.point || peelOutput.evidence) && (
                             <hr className="border-t border-green-600/20 dark:border-[#00ff00]/20 my-4" />
                         )}
                         <div className="mb-2">
                             <h4 className="font-bold text-zinc-900 dark:text-gray-100 text-[15px] mb-1">Full Prose</h4>
                             <p>{peelOutput.fullProse || peelOutput.raw?.replace(/<[^>]+>/g, '').trim()}</p>
                             <div className="mt-5">
                               <button
                                 onClick={() => {
                                   const textToCopy = peelOutput.fullProse || peelOutput.raw?.replace(/<[^>]+>/g, '').trim() || "";
                                   navigator.clipboard.writeText(textToCopy);
                                   toast.success("Paragraph copied to clipboard!");
                                 }}
                                 className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 dark:bg-[#00ff00]/10 dark:hover:bg-[#00ff00]/20 text-white dark:text-[#00ff00] font-bold rounded-lg transition-colors border border-transparent dark:border-[#00ff00]/30 shadow-md text-sm"
                               >
                                 <Copy className="w-4 h-4" /> Copy Paragraph
                               </button>
                             </div>
                         </div>
                   </div>
                </div>
             )}
          </div>
        ) : activeView === 'tos' ? (
          <TermsOfService />
        ) : null}

        <div className="text-center mt-7 space-y-1.5 pb-8">
           <p className="text-[12px] text-green-600 dark:text-[#00ff00] font-medium tracking-wide">Start generating tailored coursework absolutely for free...</p>
           <p className="text-[11px] text-green-600 dark:text-[#00ff00]/80">We leverage local document grounding and regional context bypass frameworks to produce flawless alignment.</p>
        </div>
      </main>

      {activeView === 'humanizer' && <LandingContent />}
      {activeView === 'detector' && <DetectorLandingContent />}
      {activeView === 'peel' && <PeelLandingContent />}

      {/* Style Modal */}
      {showStyleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
           <div className="bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-2xl p-6 max-w-sm w-full shadow-2xl relative">
              <h2 className="font-heading text-2xl tracking-tight font-bold mb-2 text-zinc-900 dark:text-white">Select Rewrite Style</h2>
              <p className="text-sm text-zinc-900/70 dark:text-white/70 mb-6">Choose the style you'd like the AI to use for the selected text.</p>
              
              <div className="flex flex-col gap-3 mb-6">
                 {(() => {
                    const styles = [
                       { id: 'Free', label: 'Free' },
                       { id: 'Standard', label: 'Standard' },
                       { id: 'Academic', label: 'Academic & Formal' },
                       { id: 'Simple', label: 'Simple & Direct' },
                       { id: 'Flowing', label: 'Smooth & Flowing' },
                       { id: 'Formal', label: 'Strictly Formal' },
                       { id: 'Expand', label: 'Expand Length' },
                       { id: 'Shorten', label: 'Shorten Length' }
                    ];

                    return styles.map(s => (
                       <label key={s.id} className="flex items-center gap-3 cursor-pointer p-3 border border-zinc-200 dark:border-[#2e3138] rounded-xl hover:bg-zinc-200 dark:bg-[#2e3138]/50 transition-colors">
                          <input 
                            type="radio" 
                            name="rewriteStyle" 
                            checked={rewriteStyle === s.id}
                            onChange={() => setRewriteStyle(s.id)}
                            className="w-4 h-4 text-green-600 dark:text-[#00ff00] bg-transparent border-zinc-500 focus:ring-[#00ff00] focus:ring-offset-zinc-900"
                          />
                          <span className="text-sm text-zinc-900 dark:text-white font-medium">{s.label}</span>
                       </label>
                    ));
                 })()}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setShowStyleModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-900/70 dark:text-white/70 hover:text-zinc-900 dark:hover:text-white hover:bg-white/5 transition-colors border border-transparent">
                  Cancel
                </button>
                <button 
                  onClick={handleSmartRewriteConfirm}
                  className="bg-[#00ff00] text-black px-4 py-2 rounded-lg text-sm font-bold shadow-lg shadow-[#00ff00]/20 hover:bg-opacity-90 transition-all flex items-center justify-center flex-1">
                  Rewrite
                </button>
              </div>
           </div>
        </div>
      )}

      {/* Citation Modal */}
      {showCitationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
           <div className="bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-2xl p-6 max-w-sm w-full shadow-2xl relative">
              <h2 className="font-heading text-2xl tracking-tight font-bold mb-2 text-zinc-900 dark:text-white">Generate Citation</h2>
              <p className="text-sm text-zinc-900/70 dark:text-white/70 mb-6">Enter a URL to generate a Harvard style reference.</p>
              
              <input
                type="text"
                placeholder="https://example.com"
                value={citationLink}
                onChange={(e) => setCitationLink(e.target.value)}
                className="w-full bg-zinc-50 dark:bg-[#111318] border border-zinc-200 dark:border-[#2e3138] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-green-600 dark:border-[#00ff00] transition-colors placeholder-zinc-500 mb-6"
              />

              <div className="flex justify-end gap-3">
                 <button 
                  onClick={() => setShowCitationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-zinc-900/70 dark:text-white/70 hover:text-zinc-900/90 dark:hover:text-white/90 transition-colors">
                    Cancel
                 </button>
                 <button 
                  onClick={handleGenerateCitation}
                  disabled={isGeneratingCitation || !citationLink}
                  className="bg-[#00ff00] text-black px-4 py-2 rounded-lg text-sm font-bold shadow-lg shadow-[#00ff00]/20 hover:bg-[#00ff00] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[80px]">
                    {isGeneratingCitation ? <Loader2 className="w-4 h-4 animate-spin text-black" /> : 'Generate'}
                 </button>
              </div>
           </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
         <AuthModal type={showAuthModal} onClose={() => setShowAuthModal(null)} />
      )}

      {/* Highlight Prompt Modal */}
      {showHighlightPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm pt-20">
           <div className="bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-2xl p-6 max-w-sm w-full shadow-2xl relative flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-[#00ff00]/10 flex items-center justify-center mb-4">
                 <MousePointerClick className="w-6 h-6 text-green-600 dark:text-[#00ff00]" />
              </div>
              <h2 className="font-heading text-xl tracking-tight font-bold mb-2 text-zinc-900 dark:text-white">Select text to rewrite</h2>
              <p className="text-sm text-zinc-900/70 dark:text-white/70 mb-6">
                 Please highlight the specific sentence or paragraph you'd like to rewrite in the output document.
              </p>
              <button 
                 onClick={() => setShowHighlightPrompt(false)} 
                 className="w-full px-4 py-2 bg-[#00ff00] text-black font-semibold rounded-lg hover:bg-[#00ff00] transition shadow-lg shadow-[#00ff00]/10"
              >
                 Got it
              </button>
           </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm overflow-y-auto pt-20">
           <div className="bg-zinc-100 dark:bg-[#181a1f] border border-zinc-200 dark:border-[#2e3138] rounded-2xl p-6 max-w-sm w-full shadow-2xl relative">
              <h2 className="font-heading text-2xl tracking-tight font-bold mb-2 text-zinc-900 dark:text-white">Upgrade or Quick Buy</h2>
              <p className="text-sm text-zinc-900/70 dark:text-white/70 mb-6">Need more words? Upgrade your plan to keep humanizing.</p>
              
              <div className="space-y-3 mb-6">
                 <button 
                   onClick={() => setSelectedTopup('pack_150')}
                   className={`w-full text-left rounded-xl p-4 border transition relative overflow-hidden ${selectedTopup === 'pack_150' ? 'bg-[#00ff00]/10 border-green-600 dark:border-[#00ff00]' : 'bg-zinc-50 dark:bg-[#111318] border-zinc-200 dark:border-[#2e3138] hover:border-green-600 dark:border-[#00ff00]/50'} flex justify-between items-center`}
                 >
                    {selectedTopup === 'pack_150' && <div className="absolute top-0 right-0 bg-green-600 dark:bg-[#00ff00] text-white dark:text-black text-[10px] font-bold px-2 py-0.5 rounded-bl-lg uppercase tracking-wider">Best Value</div>}
                    <div className="flex items-center gap-2">
                       <Coins className={`w-5 h-5 ${selectedTopup === 'pack_150' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900/60 dark:text-white/60'}`} />
                       <span className={`font-bold text-lg ${selectedTopup === 'pack_150' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900 dark:text-white'}`}>150 Credits Pack</span>
                    </div>
                    <div className="font-heading font-medium text-xl text-zinc-900 dark:text-white">£9.99</div>
                 </button>

                 <button 
                   onClick={() => setSelectedTopup('pack_50')}
                   className={`w-full text-left rounded-xl p-4 border transition ${selectedTopup === 'pack_50' ? 'bg-[#00ff00]/10 border-green-600 dark:border-[#00ff00]' : 'bg-zinc-50 dark:bg-[#111318] border-zinc-200 dark:border-[#2e3138] hover:border-green-600 dark:border-[#00ff00]/50'} flex justify-between items-center`}
                 >
                    <div className="flex items-center gap-2">
                       <Coins className={`w-5 h-5 ${selectedTopup === 'pack_50' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900/60 dark:text-white/60'}`} />
                       <span className={`font-bold text-lg ${selectedTopup === 'pack_50' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900 dark:text-white'}`}>50 Credits Pack</span>
                    </div>
                    <div className="font-heading font-medium text-xl text-zinc-900 dark:text-white">£3.99</div>
                 </button>

                 <button 
                   onClick={() => setSelectedTopup('stealth_15')}
                   className={`w-full text-left rounded-xl p-4 border transition ${selectedTopup === 'stealth_15' ? 'bg-[#00ff00]/10 border-green-600 dark:border-[#00ff00]' : 'bg-zinc-50 dark:bg-[#111318] border-zinc-200 dark:border-[#2e3138] hover:border-green-600 dark:border-[#00ff00]/50'} flex justify-between items-center`}
                 >
                    <div className="flex items-center gap-2">
                       <EyeOff className={`w-5 h-5 ${selectedTopup === 'stealth_15' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900/60 dark:text-white/60'}`} />
                       <div className="flex flex-col">
                          <span className={`font-bold text-lg leading-tight flex items-center gap-2 ${selectedTopup === 'stealth_15' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900 dark:text-white'}`}>
                             15 Credits
                          </span>
                       </div>
                    </div>
                    <div className="font-heading font-medium text-xl text-zinc-900 dark:text-white">£0.99</div>
                 </button>

                 <button 
                   onClick={() => setSelectedTopup('stealth_9')}
                   className={`w-full text-left rounded-xl p-4 border transition ${selectedTopup === 'stealth_9' ? 'bg-[#00ff00]/10 border-green-600 dark:border-[#00ff00]' : 'bg-zinc-50 dark:bg-[#111318] border-zinc-200 dark:border-[#2e3138] hover:border-green-600 dark:border-[#00ff00]/50'} flex justify-between items-center`}
                 >
                    <div className="flex items-center gap-2">
                       <EyeOff className={`w-5 h-5 ${selectedTopup === 'stealth_9' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900/60 dark:text-white/60'}`} />
                       <div className="flex flex-col">
                          <span className={`font-bold text-lg leading-tight flex items-center gap-2 ${selectedTopup === 'stealth_9' ? 'text-green-600 dark:text-[#00ff00]' : 'text-zinc-900 dark:text-white'}`}>
                             9 Credits
                             <span className="bg-red-500/20 text-red-400 text-[9px] uppercase px-1.5 py-0.5 rounded-sm tracking-wider font-bold">Quick Buy</span>
                          </span>
                       </div>
                    </div>
                    <div className="font-heading font-medium text-xl text-zinc-900 dark:text-white">£0.45</div>
                 </button>
              </div>

              {selectedTopup === 'pack_150' ? (
                <div className="text-xs text-zinc-900/60 dark:text-white/60 space-y-1.5 mb-6 bg-zinc-50 dark:bg-[#111318] p-3 rounded-lg border border-yellow-200 dark:border-yellow-900/30">
                   <div className="font-semibold text-zinc-900 dark:text-white mb-2 text-sm">Included in £9.99 Best Value upgrade:</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 30,000 humanizer words / mo.</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 15,000 paraphraser words / mo.</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Unlimited plagiarism & grammar checks (Fix All Errors)</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> ALL modes and settings</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 500 words per process</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Undetectable by all AIs</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Continuous improvements</div>
                </div>
              ) : selectedTopup === 'pack_50' ? (
                <div className="text-xs text-zinc-900/60 dark:text-white/60 space-y-1.5 mb-6 bg-zinc-50 dark:bg-[#111318] p-3 rounded-lg border border-zinc-200 dark:border-[#2e3138]/50">
                   <div className="font-semibold text-zinc-900 dark:text-white mb-2 text-sm">Included in £3.99 upgrade:</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 10,000 humanizer words / mo.</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 5,000 paraphraser words / mo.</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Unlimited plagiarism & grammar checks (Fix All Errors)</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> ALL modes and settings</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 500 words per process</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Undetectable by all AIs</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Continuous improvements</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> No weird or random words</div>
                </div>
              ) : (
                <div className="text-xs text-zinc-900/60 dark:text-white/60 space-y-2 mb-6 bg-zinc-50 dark:bg-[#111318] p-3 rounded-lg border border-zinc-200 dark:border-[#2e3138]/50">
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> 1 Credit = 1 Process</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Credits never expire</div>
                   <div className="flex items-start gap-2"><div className="mt-1 shrink-0 w-1 h-1 rounded-full bg-[#00ff00]"></div> Stealth Mode uses 2 credits</div>
                </div>
              )}

              <div className="flex gap-3 mb-4 flex-col sm:flex-row">
                 <button onClick={() => setShowPricing(false)} className="w-full px-4 py-3 bg-zinc-200 dark:bg-[#2e3138] text-zinc-900/80 dark:text-white/80 font-medium rounded-lg hover:bg-[#343842] transition sm:w-1/3">Cancel</button>
                 <button onClick={handleTopUp} className="w-full px-4 py-3 bg-[#00ff00] text-black font-semibold rounded-lg hover:bg-[#00ff00]/90 transition shadow-lg shadow-[#00ff00]/10 sm:flex-1 flex items-center justify-center gap-2">
                    Continue to Payment
                 </button>
              </div>

              <div className="flex flex-col items-center justify-center gap-3 mb-8">
                 <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-black dark:bg-white rounded-md border border-zinc-800 dark:border-zinc-200 shadow-sm">
                       <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 384 512" className="block text-white dark:text-black"><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                       <span className="text-[12px] font-bold text-white dark:text-black leading-none">Pay</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-black rounded-md border border-zinc-200 dark:border-zinc-800 shadow-sm">
                       <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 488 512" className="block text-black dark:text-white"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/></svg>
                       <span className="text-[12px] font-bold text-zinc-900 dark:text-white leading-none">Pay</span>
                    </div>
                 </div>
                 <div className="text-[11px] text-zinc-500 font-medium text-center">
                    Transactions secured by Stripe. Seamlessly pay with Apple Pay or Google Pay.
                 </div>
              </div>

              <div className="border-t border-zinc-200 dark:border-[#2e3138] pt-6">
                <h3 className="font-medium text-zinc-900 dark:text-white mb-4">Recent Usage</h3>
                {recentActions.length === 0 ? (
                  <p className="text-xs text-zinc-900/40 dark:text-white/40 italic">No recent actions.</p>
                ) : (
                  <div className="space-y-3">
                    {recentActions.map((action, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm border-b border-zinc-200 dark:border-[#2e3138]/50 pb-2 last:border-0">
                        <div>
                          <p className="text-zinc-900/80 dark:text-white/80 font-medium">{action.name}</p>
                          <p className="text-[10px] text-zinc-900/40 dark:text-white/40">{new Date(action.timestamp).toLocaleTimeString()}</p>
                        </div>
                        <span className="text-amber-400 font-mono">-{action.cost} Cr</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
           </div>
        </div>
      )}

      {/* Site Map / Footer */}
      
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
           <div className="bg-white dark:bg-[#1a1d24] border border-zinc-200 dark:border-[#2e3138] p-8 rounded-2xl max-w-sm w-full shadow-2xl relative">
              <button onClick={() => setShowPaymentModal(null)} className="absolute top-4 right-4 text-zinc-900/60 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white transition-colors outline-none cursor-pointer">
                 <X className="w-5 h-5 pointer-events-none" />
              </button>
              <div className="flex justify-center mb-6 text-green-600 dark:text-[#00ff00]">
                 <CheckCircle className="w-12 h-12" />
              </div>
              <h2 className="font-heading text-2xl tracking-tight font-bold mb-4 text-center text-zinc-900 dark:text-white">Ready for Checkout</h2>
              <p className="text-sm text-zinc-900/70 dark:text-white/70 mb-8 text-center leading-relaxed">
                 To complete your payment, choose an option below. Transactions are secured by Stripe.
              </p>
              
              <div className="flex flex-col gap-3">
                 <a 
                   href={showPaymentModal.url}
                   target="_blank"
                   rel="noopener noreferrer"
                   onClick={() => setShowPaymentModal(null)}
                   className="w-full px-4 py-3 bg-black text-white dark:bg-white dark:text-black font-semibold rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2"
                 >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 384 512" className="block"><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg> Pay with Apple Pay
                 </a>
                 <a 
                   href={showPaymentModal.url}
                   target="_blank"
                   rel="noopener noreferrer"
                   onClick={() => setShowPaymentModal(null)}
                   className="w-full px-4 py-3 bg-white text-black border border-zinc-200 dark:bg-black dark:text-white dark:border-[#2e3138] font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900 transition flex items-center justify-center gap-2"
                 >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 488 512" className="block"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/></svg> Pay with Google Pay
                 </a>
                 <a 
                   href={showPaymentModal.url}
                   target="_blank"
                   rel="noopener noreferrer"
                   onClick={() => setShowPaymentModal(null)}
                   className="w-full px-4 py-3 bg-[#00ff00] text-black font-semibold rounded-lg hover:bg-[#00ff00]/90 transition shadow-lg shadow-[#00ff00]/10 flex items-center justify-center gap-2"
                 >
                    <CreditCard className="w-5 h-5 block" /> Pay with Card (Stripe)
                 </a>
                 <button onClick={() => setShowPaymentModal(null)} className="py-3 text-sm text-zinc-900/60 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white transition w-full outline-none uppercase tracking-wide font-bold">
                    Cancel
                 </button>
              </div>
           </div>
        </div>
      )}

      <footer className="mt-auto py-12 px-6 border-t border-zinc-200 dark:border-[#2e3138] bg-zinc-50 dark:bg-[#111318]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-1 space-y-4">
             <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-lg bg-zinc-900 flex items-center justify-center text-[#00ff00]">
                  <Bot className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <span className="font-heading font-bold text-md tracking-tight text-zinc-900 dark:text-white">UndetectableCoursework</span>
             </div>
             <p className="text-sm text-zinc-600 dark:text-gray-400">
               Empowering students with AI tools tailored for coursework, humanization, and academic integrity.
             </p>
          </div>
          
          <div>
            <h3 className="font-bold text-zinc-900 dark:text-white mb-4 uppercase text-xs tracking-wider">Features & Tools</h3>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => { setActiveView('humanizer'); window.scrollTo(0, 0); }} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">AI Humanizer</button></li>
              <li><button onClick={() => { setActiveView('detector'); window.scrollTo(0, 0); }} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">AI Content Detector</button></li>
              <li><button onClick={() => { setActiveView('peel'); window.scrollTo(0, 0); }} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">PEEL Paragraph Generator</button></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-zinc-900 dark:text-white mb-4 uppercase text-xs tracking-wider">Account & Billing</h3>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => setShowPricing(true)} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Pricing & Top Up</button></li>
              {!user ? (
                <>
                  <li><button onClick={() => setShowAuthModal('login')} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Login</button></li>
                  <li><button onClick={() => setShowAuthModal('signup')} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Create Account</button></li>
                </>
              ) : (
                <li><button onClick={handleLogout} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Sign Out</button></li>
              )}
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-zinc-900 dark:text-white mb-4 uppercase text-xs tracking-wider">Resources</h3>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => setShowCitationModal(true)} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Citation Generator</button></li>
              <li><button onClick={() => { setActiveView('tos'); window.scrollTo(0, 0); }} className="text-zinc-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-[#00ff00] transition">Terms of Service</button></li>
              <li><span className="text-zinc-500 dark:text-gray-400 cursor-not-allowed">Privacy Policy</span></li>
            </ul>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto mt-12 pt-8 border-t border-zinc-200 dark:border-[#2e3138] text-center text-xs text-zinc-500 dark:text-gray-400">
          &copy; {new Date().getFullYear()} UndetectableCoursework. Site Map generated. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
